// Google-Docs-style Share for the editor header: add people as Editor /
// Viewer / Viewer · no download, see and change who has access, copy the
// file's link, and — for the owner — manage share links. Mirrors Drive's
// ShareDialog. Identical file in Docs, Sheets and Slides.

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useAuth } from "react-oidc-context";
import { Check, Copy, Globe, Link2, Loader2, Lock, MessageSquare, Trash2, UserPlus, Users, Video, X } from "@fg/icons";
import { BTN_GHOST, BTN_OUTLINE, BTN_PRIMARY, Modal, SELECT, cx } from "@/features/home/HomeUi";
import {
    ACCESS_LABEL, ACCESS_ORDER, copyText, driveFileApi, driveLinks, errorText, fromAccess, looksLikeEmail, toAccess,
    type Access, type ItemSharing, type LinkAudience, type PersonAccess, type ShareLink
} from "./driveFileApi";

export interface ShareTarget {
    id: string;
    name: string;
}

/**
 * "onPrimary" sits on the suite top bar (SuiteAppBar — Accent, Light or Dark
 * style, so it uses the `.suite-appbar-*` classes); "surface" on a plain toolbar.
 */
export type HeaderTone = "onPrimary" | "surface";

const ACCESS_HINT: Record<Access, string> = {
    editor: "Editors can change, rename and share it.",
    viewer: "Viewers can open and download it.",
    "viewer-nodl": "Viewers can open it but not download it."
};

const SMALL_SELECT =
    "h-8 rounded-md border border-input bg-background px-1.5 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60";
const TEXT_INPUT =
    "h-9 w-full rounded-md border border-input bg-background px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";
const ICON_BTN =
    "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50";

/** The header's Share button. Disabled (with a reason) until the file is in Drive. */
export function ShareButton({ file, tone }: { file: ShareTarget | null; tone: HeaderTone }) {
    const [open, setOpen] = useState(false);
    const hintId = useId();
    const disabled = file === null;
    return (
        <>
            <button
                type="button"
                onClick={() => { if (!disabled) setOpen(true); }}
                aria-label="Share"
                aria-haspopup="dialog"
                aria-disabled={disabled || undefined}
                aria-describedby={disabled ? hintId : undefined}
                title={disabled ? "Save to Drive first" : "Share"}
                className={cx(
                    tone === "onPrimary"
                        ? "suite-appbar-share shrink-0"
                        : "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-full bg-primary px-3 text-sm font-medium text-primary-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
                    disabled
                        ? "cursor-not-allowed opacity-60"
                        : tone === "onPrimary" ? undefined : "hover:bg-primary/90"
                )}
            >
                {disabled ? <Lock className="h-4 w-4" aria-hidden /> : <UserPlus className="h-4 w-4" aria-hidden />}
                <span className="hidden sm:inline">Share</span>
            </button>
            {disabled ? <span id={hintId} hidden>Save to Drive first</span> : null}
            {open && file ? <ShareDialog file={file} onClose={() => setOpen(false)} /> : null}
        </>
    );
}

export function ShareDialog({ file, onClose }: { file: ShareTarget; onClose: () => void }) {
    const auth = useAuth();
    const tokenRef = useRef(auth.user?.access_token);
    tokenRef.current = auth.user?.access_token;
    const myEmail = (auth.user?.profile.email ?? "").toLowerCase();
    const myDomain = myEmail.includes("@") ? myEmail.slice(myEmail.indexOf("@") + 1) : "your organization";

    const [sharing, setSharing] = useState<ItemSharing | null>(null);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);
    const peopleHeadingId = useId();
    const doneRef = useRef<HTMLButtonElement>(null);
    const timersRef = useRef<number[]>([]);
    useEffect(() => () => timersRef.current.forEach((t) => window.clearTimeout(t)), []);
    const later = (fn: () => void, ms: number) => { timersRef.current.push(window.setTimeout(fn, ms)); };

    const load = useCallback(async () => {
        try {
            setSharing(await driveFileApi.sharing(file.id, tokenRef.current));
            setLoadError(null);
        } catch (err) {
            setLoadError(errorText(err));
        }
    }, [file.id]);
    useEffect(() => { void load(); }, [load]);

    const canManage = sharing?.canManage ?? false;
    // Shown while loading so the email box can take focus straight away.
    const showAdd = canManage || (sharing === null && loadError === null);

    // A viewer gets no "Add people" box — move focus off it once we know.
    useEffect(() => {
        if (sharing && !sharing.canManage) doneRef.current?.focus();
    }, [sharing]);

    // ── Add people ──
    const [chips, setChips] = useState<string[]>([]);
    const [draft, setDraft] = useState("");
    const [newAccess, setNewAccess] = useState<Access>("viewer");
    const [adding, setAdding] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    const commitDraft = (): { emails: string[]; bad: string[] } => {
        const parts = draft.split(/[\s,;]+/).map((p) => p.trim().toLowerCase()).filter(Boolean);
        const bad = parts.filter((p) => !looksLikeEmail(p));
        const emails = [...chips];
        for (const p of parts) if (looksLikeEmail(p) && !emails.includes(p)) emails.push(p);
        setChips(emails);
        setDraft(bad.join(" "));
        setError(bad.length ? `Not an email address: ${bad.join(", ")}` : null);
        return { emails, bad };
    };

    const submitPeople = async () => {
        const { emails, bad } = commitDraft();
        if (bad.length) { inputRef.current?.focus(); return; }
        if (emails.length === 0) {
            setError("Add at least one email address.");
            inputRef.current?.focus();
            return;
        }
        setAdding(true);
        setError(null);
        try {
            const next = await driveFileApi.addPeople(file.id, { emails, ...fromAccess(newAccess) }, tokenRef.current);
            if (next && Array.isArray(next.people)) setSharing(next);
            else await load();
            setChips([]);
            setDraft("");
            setNotice(emails.length === 1 ? `Shared with ${emails[0]}` : `Shared with ${emails.length} people`);
            later(() => setNotice(null), 4000);
        } catch (err) {
            setError(errorText(err));
        } finally {
            setAdding(false);
        }
    };

    const changeAccess = async (person: PersonAccess, access: Access) => {
        setBusyId(person.id);
        setError(null);
        try {
            await driveFileApi.updatePerson(person.id, fromAccess(access), tokenRef.current);
            await load();
        } catch (err) {
            setError(errorText(err));
        } finally {
            setBusyId(null);
        }
    };

    const removePerson = async (person: PersonAccess) => {
        setBusyId(person.id);
        setError(null);
        try {
            await driveFileApi.removePerson(person.id, tokenRef.current);
            await load();
            setNotice(`Removed ${person.email}`);
            later(() => setNotice(null), 4000);
        } catch (err) {
            setError(errorText(err));
        } finally {
            setBusyId(null);
        }
    };

    const copyItemLink = async () => {
        if (await copyText(driveLinks.open(file.id))) {
            setCopied(true);
            later(() => setCopied(false), 2000);
        } else {
            setError("Couldn't copy — the browser blocked the clipboard.");
        }
    };

    return (
        <Modal
            title={`Share “${file.name}”`}
            onClose={onClose}
            footer={
                <>
                    <button
                        type="button"
                        onClick={() => void copyItemLink()}
                        title={`Copy ${driveLinks.open(file.id)} — opens for anyone who has access`}
                        className={cx(BTN_GHOST, "mr-auto text-primary")}
                    >
                        {copied ? <Check className="h-4 w-4" aria-hidden /> : <Link2 className="h-4 w-4" aria-hidden />}
                        {copied ? "Link copied" : "Copy link"}
                    </button>
                    <button ref={doneRef} type="button" onClick={onClose} className={BTN_OUTLINE}>Done</button>
                </>
            }
        >
            <div className="max-h-[65vh] space-y-4 overflow-y-auto p-4">
                {showAdd ? (
                    <section aria-label="Add people" className="space-y-2">
                        <div className="flex flex-wrap items-start gap-2">
                            <div
                                className="flex min-h-9 min-w-0 flex-1 basis-52 flex-wrap items-center gap-1 rounded-md border border-input bg-background px-2 py-1 focus-within:ring-2 focus-within:ring-ring"
                                onClick={() => inputRef.current?.focus()}
                            >
                                {chips.map((c) => (
                                    <span key={c} className="flex max-w-full items-center gap-1 rounded-full bg-primary/10 py-0.5 pl-2 pr-1 text-xs text-primary">
                                        <span className="truncate">{c}</span>
                                        <button
                                            type="button"
                                            onClick={(e) => { e.stopPropagation(); setChips((cs) => cs.filter((x) => x !== c)); }}
                                            className="rounded-full p-0.5 hover:bg-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                            aria-label={`Remove ${c}`}
                                        >
                                            <X className="h-3 w-3" aria-hidden />
                                        </button>
                                    </span>
                                ))}
                                <input
                                    ref={inputRef}
                                    data-autofocus
                                    type="email"
                                    value={draft}
                                    disabled={adding}
                                    onChange={(e) => { setDraft(e.target.value); setError(null); }}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            e.preventDefault();
                                            if (draft.trim()) commitDraft();
                                            else if (chips.length) void submitPeople();
                                        } else if (e.key === "," || e.key === ";" || e.key === " ") {
                                            if (draft.trim()) { e.preventDefault(); commitDraft(); }
                                        } else if (e.key === "Backspace" && !draft && chips.length) {
                                            setChips((cs) => cs.slice(0, -1));
                                        }
                                    }}
                                    onBlur={() => { if (draft.trim()) commitDraft(); }}
                                    placeholder={chips.length ? "" : "Add people by email"}
                                    aria-label="Add people by email"
                                    autoComplete="off"
                                    className="min-w-[8rem] flex-1 bg-transparent py-1 text-sm text-foreground outline-none placeholder:text-muted-foreground"
                                />
                            </div>
                            <select
                                value={newAccess}
                                onChange={(e) => setNewAccess(e.target.value as Access)}
                                aria-label="Access for the people you add"
                                className={SELECT}
                            >
                                {ACCESS_ORDER.map((a) => <option key={a} value={a}>{ACCESS_LABEL[a]}</option>)}
                            </select>
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-xs text-muted-foreground">{ACCESS_HINT[newAccess]}</p>
                            <button
                                type="button"
                                onClick={() => void submitPeople()}
                                disabled={adding || sharing === null || (!chips.length && !draft.trim())}
                                className={BTN_PRIMARY}
                            >
                                {adding ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <UserPlus className="h-4 w-4" aria-hidden />}
                                Share
                            </button>
                        </div>
                    </section>
                ) : null}

                {notice ? (
                    <p role="status" className="flex items-center gap-1 text-xs text-emerald-600 dark:text-emerald-400">
                        <Check className="h-3.5 w-3.5" aria-hidden /> {notice}
                    </p>
                ) : null}
                {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}

                <section aria-labelledby={peopleHeadingId}>
                    <h3 id={peopleHeadingId} className="mb-2 text-sm font-semibold">People with access</h3>
                    {sharing === null ? (
                        loadError ? (
                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                <span role="alert" className="text-destructive">{loadError}</span>
                                <button
                                    type="button"
                                    onClick={() => { setLoadError(null); void load(); }}
                                    className="font-medium text-primary hover:underline"
                                >
                                    Retry
                                </button>
                            </div>
                        ) : (
                            <p className="flex items-center gap-2 text-xs text-muted-foreground">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Loading…
                            </p>
                        )
                    ) : (
                        <ul className="space-y-1">
                            <PersonRow
                                email={sharing.ownerEmail}
                                you={sharing.ownerEmail.toLowerCase() === myEmail}
                                trailing={<span className="px-1 text-xs text-muted-foreground">Owner</span>}
                            />
                            {sharing.people.map((p) => {
                                const busy = busyId === p.id;
                                return (
                                    <PersonRow
                                        key={p.id}
                                        email={p.email}
                                        you={p.email.toLowerCase() === myEmail}
                                        source={p.source}
                                        trailing={canManage ? (
                                            <span className="flex items-center gap-1">
                                                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" aria-hidden /> : null}
                                                <select
                                                    value={toAccess(p.role, p.allowDownload)}
                                                    disabled={busy}
                                                    onChange={(e) => void changeAccess(p, e.target.value as Access)}
                                                    aria-label={`Access for ${p.email}`}
                                                    className={SMALL_SELECT}
                                                >
                                                    {ACCESS_ORDER.map((a) => <option key={a} value={a}>{ACCESS_LABEL[a]}</option>)}
                                                </select>
                                                <button
                                                    type="button"
                                                    onClick={() => void removePerson(p)}
                                                    disabled={busy}
                                                    aria-label={`Remove access for ${p.email}`}
                                                    title="Remove access"
                                                    className={cx(ICON_BTN, "hover:text-destructive")}
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" aria-hidden />
                                                </button>
                                            </span>
                                        ) : (
                                            <span className="px-1 text-xs text-muted-foreground">{ACCESS_LABEL[toAccess(p.role, p.allowDownload)]}</span>
                                        )}
                                    />
                                );
                            })}
                        </ul>
                    )}
                    {sharing && !canManage ? (
                        <p className="mt-2 text-xs text-muted-foreground">Only the owner and editors can change who has access.</p>
                    ) : null}
                </section>

                {/* Links — only the owner manages those (the BFF scopes them to their creator). */}
                {sharing?.myRole === "owner" ? <LinkSection fileId={file.id} myDomain={myDomain} /> : null}
            </div>
        </Modal>
    );
}

function PersonRow({
    email, you, source, trailing
}: {
    email: string;
    you: boolean;
    source?: string | null;
    trailing: ReactNode;
}) {
    return (
        <li className="flex items-center gap-3 rounded-md px-1 py-1.5">
            <span
                aria-hidden
                className={cx("flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-semibold text-white", colourFor(email))}
            >
                {(email[0] ?? "?").toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
                <span className="block truncate text-sm" title={email}>
                    {email}{you ? <span className="text-muted-foreground"> (you)</span> : null}
                </span>
                {source && source !== "drive" ? (
                    <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
                        {source === "connect-recording" ? <Video className="h-3 w-3" aria-hidden /> : <MessageSquare className="h-3 w-3" aria-hidden />}
                        {source === "connect-recording" ? "Meeting recording"
                            : source === "connect-channel" ? "Shared in a Connect channel" : "Shared in a Connect chat"}
                    </span>
                ) : null}
            </span>
            <span className="shrink-0">{trailing}</span>
        </li>
    );
}

/** Share links: anyone in your organisation, or anyone with the link (optional password + expiry). */
function LinkSection({ fileId, myDomain }: { fileId: string; myDomain: string }) {
    const auth = useAuth();
    const tokenRef = useRef(auth.user?.access_token);
    tokenRef.current = auth.user?.access_token;
    const idBase = useId();

    const [links, setLinks] = useState<ShareLink[] | null>(null);
    const [mode, setMode] = useState<LinkAudience>("domain");
    const [permission, setPermission] = useState<"view" | "edit">("view");
    const [password, setPassword] = useState("");
    const [expiry, setExpiry] = useState("");
    const [creating, setCreating] = useState(false);
    const [busyId, setBusyId] = useState<string | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            setLinks(await driveFileApi.links(fileId, tokenRef.current));
        } catch (err) {
            setLinks([]);
            setError(errorText(err));
        }
    }, [fileId]);
    useEffect(() => { void load(); }, [load]);

    const copy = async (link: ShareLink) => {
        if (await copyText(driveLinks.shareLink(link.token))) {
            setCopiedId(link.id);
            window.setTimeout(() => setCopiedId((c) => (c === link.id ? null : c)), 2000);
        } else {
            setError("Couldn't copy — the browser blocked the clipboard.");
        }
    };

    const create = async () => {
        setCreating(true);
        setError(null);
        try {
            const link = await driveFileApi.createLink(
                fileId,
                {
                    permission,
                    audience: mode,
                    password: mode === "anonymous" && password.trim() ? password.trim() : undefined,
                    // End of the chosen day in the viewer's time zone ("YYYY-MM-DD"
                    // alone parses as UTC midnight — a day early east of UTC).
                    expiresAt: mode === "anonymous" && expiry ? new Date(`${expiry}T23:59:59`).toISOString() : undefined
                },
                tokenRef.current
            );
            setPassword("");
            setExpiry("");
            await load();
            await copy(link);
        } catch (err) {
            setError(errorText(err));
        } finally {
            setCreating(false);
        }
    };

    const remove = async (link: ShareLink) => {
        setBusyId(link.id);
        setError(null);
        try {
            await driveFileApi.deleteLink(link.id, tokenRef.current);
            setLinks((ls) => (ls ?? []).filter((l) => l.id !== link.id));
        } catch (err) {
            setError(errorText(err));
        } finally {
            setBusyId(null);
        }
    };

    const audienceLabel = (l: ShareLink) =>
        l.audience === "email" ? (l.allowedEmails ?? "Specific people")
            : l.audience === "domain" ? `People in ${myDomain}` : "Anyone with the link";

    return (
        <section aria-labelledby={`${idBase}-heading`} className="space-y-2 border-t pt-4">
            <h3 id={`${idBase}-heading`} className="flex items-center gap-1.5 text-sm font-semibold">
                <Link2 className="h-4 w-4" aria-hidden /> Links
            </h3>
            <div className="flex flex-wrap items-center gap-2">
                <select
                    value={mode}
                    onChange={(e) => setMode(e.target.value as LinkAudience)}
                    aria-label="Who can open the link"
                    className={SELECT}
                >
                    <option value="domain">{`People in ${myDomain}`}</option>
                    <option value="anonymous">Anyone with the link</option>
                </select>
                <select
                    value={permission}
                    onChange={(e) => setPermission(e.target.value as "view" | "edit")}
                    aria-label="Link permission"
                    className={SELECT}
                >
                    <option value="view">Can view</option>
                    <option value="edit">Can edit</option>
                </select>
                <button type="button" onClick={() => void create()} disabled={creating} className={BTN_OUTLINE}>
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Link2 className="h-4 w-4" aria-hidden />}
                    Create & copy link
                </button>
            </div>
            {mode === "anonymous" ? (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <label className="text-xs text-muted-foreground" htmlFor={`${idBase}-pw`}>
                        <span className="mb-1 flex items-center gap-1"><Lock className="h-3 w-3" aria-hidden /> Password (optional)</span>
                        <input
                            id={`${idBase}-pw`}
                            type="password"
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={TEXT_INPUT}
                        />
                    </label>
                    <label className="text-xs text-muted-foreground" htmlFor={`${idBase}-exp`}>
                        <span className="mb-1 block">Expires after (optional)</span>
                        <input
                            id={`${idBase}-exp`}
                            type="date"
                            value={expiry}
                            onChange={(e) => setExpiry(e.target.value)}
                            className={TEXT_INPUT}
                        />
                    </label>
                </div>
            ) : null}
            {error ? <p role="alert" className="text-xs text-destructive">{error}</p> : null}
            {links === null ? (
                <p className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Loading links…
                </p>
            ) : links.length === 0 ? (
                <p className="text-xs text-muted-foreground">No links yet. People you add above can always use “Copy link”.</p>
            ) : (
                <ul className="space-y-1">
                    {links.map((l) => (
                        <li key={l.id} className="flex items-center gap-2 rounded-md border px-2 py-1.5 text-xs">
                            {l.audience === "anonymous"
                                ? <Globe className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                                : <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />}
                            <span className="min-w-0 flex-1 truncate">
                                {audienceLabel(l)}
                                <span className="text-muted-foreground">
                                    {" · "}{l.permission === "edit" ? "can edit" : "can view"}
                                    {l.hasPassword ? " · password" : ""}
                                    {l.expiresAt ? ` · until ${new Date(l.expiresAt).toLocaleDateString()}` : ""}
                                    {` · ${l.accessCount} open${l.accessCount === 1 ? "" : "s"}`}
                                </span>
                            </span>
                            <button
                                type="button"
                                onClick={() => void copy(l)}
                                className={cx(ICON_BTN, "h-7 w-7")}
                                aria-label={`Copy link: ${audienceLabel(l)}`}
                                title="Copy link"
                            >
                                {copiedId === l.id
                                    ? <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" aria-hidden />
                                    : <Copy className="h-3.5 w-3.5" aria-hidden />}
                            </button>
                            <button
                                type="button"
                                onClick={() => void remove(l)}
                                disabled={busyId === l.id}
                                className={cx(ICON_BTN, "h-7 w-7 hover:text-destructive")}
                                aria-label={`Delete link: ${audienceLabel(l)}`}
                                title="Delete link"
                            >
                                {busyId === l.id
                                    ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                                    : <Trash2 className="h-3.5 w-3.5" aria-hidden />}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}

const AVATAR_COLOURS = ["bg-sky-600", "bg-emerald-600", "bg-violet-600", "bg-amber-600", "bg-rose-600", "bg-teal-600", "bg-indigo-600"];

function colourFor(email: string): string {
    let h = 0;
    for (let i = 0; i < email.length; i++) h = (h * 31 + email.charCodeAt(i)) | 0;
    return AVATAR_COLOURS[Math.abs(h) % AVATAR_COLOURS.length] ?? "bg-sky-600";
}
